// ════════════════════════════════════════════════════════════
// Palette commands — construction des sections (Sprint Shell)
// ════════════════════════════════════════════════════════════
// Logique PURE (sans React) → testable directement. La
// CommandPalette consomme le résultat et branche les handlers.
// ════════════════════════════════════════════════════════════
import type { AppRole } from '@/types/auth';
import type { NavItem } from '@/config/navigation';
import type { RecentPage } from '@/stores/useShellStore';

/** Identifiants stables des actions globales du shell. */
export type ShellActionId = 'toggle-theme' | 'toggle-sidebar' | 'go-account' | 'logout';

export interface ShellAction {
  id: ShellActionId;
  label: string;
}

export interface PaletteSections {
  navigation: NavItem[];
  recents: RecentPage[];
  actions: ShellAction[];
}

interface BuildArgs {
  role: AppRole | null;
  /** Items de nav déjà filtrés par rôle (cf. useVisibleNavItems). */
  navItems: NavItem[];
  recents: RecentPage[];
  /** Pathname courant : exclu des récents. */
  currentPath: string;
}

/**
 * Construit les 3 sections de la palette.
 * - navigation : items autorisés (passés déjà filtrés).
 * - recents : 5 dernières pages, route courante exclue.
 * - actions : globales ; « Basculer le thème » masqué pour le pointeur
 *   (mode clair forcé — cf. Sprint 5).
 */
export function buildPaletteSections({
  role,
  navItems,
  recents,
  currentPath,
}: BuildArgs): PaletteSections {
  const actions: ShellAction[] = [];
  if (role !== 'pointeur') {
    actions.push({ id: 'toggle-theme', label: 'Basculer le thème' });
  }
  actions.push(
    { id: 'toggle-sidebar', label: 'Replier / déplier la sidebar' },
    { id: 'go-account', label: 'Aller à Mon compte' },
    { id: 'logout', label: 'Se déconnecter' },
  );

  return {
    navigation: navItems,
    recents: recents.filter((p) => p.path !== currentPath).slice(0, 5),
    actions,
  };
}
